import React from 'react';
import styled from 'styled-components';
import { InfoIcon } from 'src/common/InfoIcon';

// The shared "variable pill" chrome behind every formula surface: the read-only
// VariableCapsule (FormulaPreview), the editable VariableChip (Tiptap) and the AI
// mining view (AiFormulaView). It owns the one cross-app invariant — a pill whose
// referenced variable has NO value renders red (dropZoneErr bg/border + a red
// info-circle replacing the type icon). Variable resolution stays with each caller
// (app `Variable` vs `AiVariable` vs a Tiptap node) — this component is purely
// presentational, so don't reach in and override `background`/`border`: that red↔blue
// treatment is the whole point of sharing it.
//
// Knobs the surfaces legitimately differ on (radius/padding/gap/border/tag) are props
// with the VariableCapsule defaults; anything more exotic (Tiptap white-space, AI
// height) is layered on via `styled(VariablePill)`, whose className reaches the root
// pill.

const Pill = styled.div<{
  $unpopulated?: boolean;
  $bordered: boolean;
  $radius: number;
  $padding: string;
  $gap: number;
}>`
  display: inline-flex;
  align-items: center;
  grid-gap: ${({ $gap }) => $gap}px;
  border-radius: ${({ $radius }) => $radius}px;
  padding: ${({ $padding }) => $padding};
  background-color: ${({ theme, $unpopulated }) =>
    $unpopulated ? theme.colors.dropZoneErrBg : theme.colors.aliceBlue};
  /* Unpopulated always draws the red border (the alert needs it); the populated
     border is opt-out (none, not a transparent 1px) so the AI chips stay the same
     height as their borderless constant-pill neighbours in the same formula row. */
  border: ${({ theme, $unpopulated, $bordered }) =>
    $unpopulated
      ? `1px solid ${theme.colors.dropZoneErrBorder}`
      : $bordered
        ? `1px solid ${theme.colors.stroke}`
        : 'none'};

  .vp-icon {
    display: inline-flex;
    align-items: center;
  }
`;

interface VariablePillProps {
  /** Populated-state icon (caller-resolved). Swapped for a red info-circle when `unpopulated`. */
  icon?: React.ReactNode;
  /** Red "missing value" treatment. */
  unpopulated?: boolean;
  /** Label content — the caller owns its typography (Poppins, or bare editor text). */
  children: React.ReactNode;
  radius?: number;
  padding?: string;
  gap?: number;
  /** Populated-state stroke border. Off → transparent border (AI), red border still wins when unpopulated. */
  bordered?: boolean;
  /** Root element — `span` for inline/Tiptap usage, `div` otherwise. (Not `as`: that is reserved by styled-components.) */
  tag?: 'div' | 'span';
  className?: string;
}

export const VariablePill: React.FC<VariablePillProps & React.HTMLAttributes<HTMLElement>> = ({
  icon,
  unpopulated,
  children,
  radius = 20,
  padding = '2px 10px',
  gap = 6,
  bordered = true,
  tag = 'div',
  ...rest
}) => (
  <Pill
    as={tag}
    $unpopulated={unpopulated}
    $bordered={bordered}
    $radius={radius}
    $padding={padding}
    $gap={gap}
    {...rest}
  >
    {(icon || unpopulated) && (
      <span className="vp-icon" data-cy={unpopulated ? 'no-data' : undefined}>
        {unpopulated ? (
          <InfoIcon color="statusRed" cursorDefault style={{ width: 16, height: 16, minWidth: 16 }} />
        ) : (
          icon
        )}
      </span>
    )}
    {children}
  </Pill>
);
